import { z } from 'zod';
import { envelope, IsoDateTime, Uuid } from './common.js';
import { defineRoute, IdParam } from './route.js';

export const SlackMilestone = z.enum(['REQUEST_RECEIVED', 'REQUEST_ACCEPTED', 'REQUEST_REJECTED', 'REQUEST_RESOLVED', 'REQUEST_NEEDS_ATTENTION', 'WORK_STARTED', 'WORK_BLOCKED', 'WORK_DELIVERED']);
export type SlackMilestone = z.infer<typeof SlackMilestone>;

export const SlackDestinationView = z.object({
  id: Uuid,
  scopeKind: z.enum(['PROJECT', 'TEAM']),
  scopeId: Uuid,
  workspaceId: z.string(),
  channelId: z.string(),
  approvedForRestricted: z.boolean(),
  milestones: z.array(z.string()),
  createdAt: IsoDateTime,
});
export type SlackDestinationView = z.infer<typeof SlackDestinationView>;

export const SetSlackDestination = z
  .object({
    scopeKind: z.enum(['PROJECT', 'TEAM']),
    scopeId: Uuid,
    workspaceId: z.string().regex(/^T[A-Z0-9]{2,20}$/),
    channelId: z.string().regex(/^[CG][A-Z0-9]{2,20}$/),
    approvedForRestricted: z.boolean(),
    milestones: z.array(SlackMilestone).min(1).max(8),
  })
  .strict();

export const SlackLinkView = z.object({ workspaceId: z.string(), slackHandle: z.string() });

export const slackRoutes = {
  getSlackLink: defineRoute({ operationId: 'getSlackLink', method: 'GET', path: '/slack/link', summary: 'Pending Slack account link (single-use state)', tag: 'slack', auth: 'session', query: z.object({ state: z.string().min(20).max(100) }), response: envelope(SlackLinkView), successStatus: 200 }),
  confirmSlackLink: defineRoute({ operationId: 'confirmSlackLink', method: 'POST', path: '/slack/link', summary: 'Explicitly link the Slack user to the signed-in account', tag: 'slack', auth: 'session', body: z.object({ state: z.string().min(20).max(100) }).strict(), response: envelope(z.object({ ok: z.literal(true) })), successStatus: 200 }),
  listSlackDestinations: defineRoute({ operationId: 'listSlackDestinations', method: 'GET', path: '/admin/slack/destinations', summary: 'Slack progress destinations', tag: 'slack', auth: 'session', response: envelope(z.array(SlackDestinationView)), successStatus: 200 }),
  setSlackDestination: defineRoute({ operationId: 'setSlackDestination', method: 'PUT', path: '/admin/slack/destinations', summary: 'Create or update an approved destination', tag: 'slack', auth: 'session', body: SetSlackDestination, response: envelope(z.array(SlackDestinationView)), successStatus: 200 }),
  deleteSlackDestination: defineRoute({ operationId: 'deleteSlackDestination', method: 'DELETE', path: '/admin/slack/destinations/:id', summary: 'Remove a destination', tag: 'slack', auth: 'session', params: IdParam, successStatus: 204 }),
} as const;
